import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Modal, Box, Dialog } from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import { Page } from 'components';
import { useDevices, useFields, useOrganizations, useProperties } from 'hooks';
import _ from 'lodash';

import { DashboardHelpers } from 'utils';

import { DeviceListView, DeviceEditModalForm } from './components';
import DevicesEditModalForm from './components/ModalForms/DevicesEditModalForm';
import DeviceDetails from './components/DeviceDetails';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.white',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  },
}));

const DeviceManagementPage = () => {
  const [openModel, setOpenModel] = useState('');
  const [selectionForModel, setSelectionForModel] = useState([]);
  const [clearSelected, setClearSelected] = useState(false);
  const [showDeviceDetails, setShowDeviceDetails] = useState(false);
  const { selectedPropertyId } = useProperties();
  const { getDevicesWithFieldNameList, saveDevices } = useDevices();
  const { selectedFieldId, fields, fetchFields } = useFields();

  const { selectedOrganizationId } = useOrganizations();
  const classes = useStyles();

  let devicesToList = getDevicesWithFieldNameList;

  useEffect(() => {
    // If we don't have fields on the first load
    // in order to have their names we need to at
    // least attempt to load them
    if (!fields || fields?.length === 0) {
      fetchFields();
    }
  }, []);

  const assignedDevices = [];
  const unassignedDevices = [];
  const { t } = useTranslation();

  if (selectedFieldId && selectedFieldId?.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
    devicesToList = _.filter(devicesToList, { field_id: selectedFieldId });
  } else if (selectedPropertyId) {
    devicesToList = _.filter(devicesToList, { property_id: selectedPropertyId });
  } else if (selectedOrganizationId) {
    devicesToList = _.filter(devicesToList, { organization_id: selectedOrganizationId });
  }

  _.forEach(devicesToList, (device) => {
    if (!!device.field_id && device.field_id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
      assignedDevices.push(device);
    } else {
      unassignedDevices.push(device);
    }
  });

  const openModelHandler = (model, devices) => {
    if (model === 'device') {
      setSelectionForModel(devices[0]);
    } else if (model === 'devices') {
      setSelectionForModel(devices);
    }
    setOpenModel(model);
  };

  const removeSelectedDevice = (device) => {
    let currentSelectedDevices = [...selectionForModel];
    _.remove(currentSelectedDevices, function (value) {
      return device.id === value.id;
    });
    setSelectionForModel(currentSelectedDevices);
  };

  const devicesModifySubmitHandler = (modifications) => {
    saveDevices(modifications);
    setClearSelected(true);
  };

  const handleHide = () => {
    setShowDeviceDetails(false);
  };

  return (
    <Page
      title={t('device_management')}
      className={classes.root}
      key={selectedOrganizationId + selectedPropertyId + selectedFieldId}
    >
      <Modal
        open={openModel === 'device'}
        onClose={() => {}}
        aria-labelledby="modal-device-edit"
        aria-describedby="modal-device-edit-form"
      >
        <Box>
          <DeviceEditModalForm
            show={openModel === 'device'}
            device={selectionForModel}
            submitEdits={devicesModifySubmitHandler}
            hide={() => setOpenModel('')}
            selectedOrganizationId={selectedOrganizationId}
          />
        </Box>
      </Modal>
      <Modal
        open={openModel === 'devices'}
        onClose={() => {}}
        aria-labelledby="modal-device-edit"
        aria-describedby="modal-device-edit-form"
      >
        <Box>
          <DevicesEditModalForm
            show={openModel === 'devices'}
            selectedDevices={selectionForModel}
            submitEdits={devicesModifySubmitHandler}
            hide={() => setOpenModel('')}
            devices={getDevicesWithFieldNameList}
            removeSelectedDevice={removeSelectedDevice}
            selectedOrganizationId={selectedOrganizationId}
          />
        </Box>
      </Modal>

      <DeviceListView
        assignedDevices={assignedDevices}
        unassignedDevices={unassignedDevices}
        openModelHandler={openModelHandler}
        selectionForModel={selectionForModel}
        setClearSelected={setClearSelected}
        clearSelected={clearSelected}
        setShowDeviceDetails={setShowDeviceDetails}
        setSelection={setSelectionForModel}
      />

      <Dialog
        disableEnforceFocus
        style={{ pointerEvents: 'none' }}
        PaperProps={{ style: { pointerEvents: 'auto' } }}
        open={showDeviceDetails}
        hideBackdrop
        onClose={() => {}}
      >
        <div>{!!showDeviceDetails && <DeviceDetails device={selectionForModel} hide={() => handleHide()} />}</div>
      </Dialog>
    </Page>
  );
};

export default DeviceManagementPage;
